import { SlinguAvatar, SfxToggle, useSfx } from '@slingu/ui';
import { useEffect, useMemo, useRef, useState } from 'react';

import { commitScore, getHighScore } from './highscores.js';
import { samplePairsForRound } from './vocabulary.js';

const HIGH_SCORE_ID = 'word-match';

export type WordMatchProps = {
  onExit: (result: { score: number; rounds: number; moves: number; xp: number }) => void;
};

type Phase = 'menu' | 'playing' | 'over';

type Card = {
  id: string;
  pairId: string;
  text: string;
  side: 'pt' | 'en';
  state: 'down' | 'up' | 'matched' | 'wrong';
};

const PAIRS_PER_ROUND = 6; // 12 cards in a 3x4 grid
const TOTAL_ROUNDS = 4;
const XP_PER_MATCH = 10;
const XP_TIME_BONUS_CAP = 40; // faster = up to 40 XP extra per round

/**
 * Word Match — pairs memory game. O jogador vira cartas pra encontrar pares
 * PT↔EN. Acertou o par, as duas ficam viradas. Errou, viram pra baixo e
 * tenta de novo. 4 rodadas de 6 pares cada. XP por par + bônus de tempo.
 */
export function WordMatch({ onExit }: WordMatchProps) {
  const sfx = useSfx();
  const sfxRef = useRef(sfx);
  sfxRef.current = sfx;

  const [phase, setPhase] = useState<Phase>('menu');
  const [round, setRound] = useState(1);
  const [cards, setCards] = useState<Card[]>([]);
  const [flipped, setFlipped] = useState<string[]>([]);
  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState<number>(() => getHighScore(HIGH_SCORE_ID));
  const [newRecord, setNewRecord] = useState<boolean>(false);
  const [roundStart, setRoundStart] = useState(0);
  const [lockBoard, setLockBoard] = useState(false);

  // Shuffle utility
  function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j]!, a[i]!];
    }
    return a;
  }

  function buildRound(roundNum: number) {
    // Amostra 6 pares do pool — dificuldade sobe a cada rodada.
    const slice = samplePairsForRound(PAIRS_PER_ROUND, roundNum, TOTAL_ROUNDS);
    const deck: Card[] = [];
    slice.forEach((pair, i) => {
      const pairId = `p${roundNum}-${i}`;
      deck.push({
        id: `${pairId}-pt`,
        pairId,
        text: pair.promptPt ?? pair.correct,
        side: 'pt',
        state: 'down',
      });
      deck.push({
        id: `${pairId}-en`,
        pairId,
        text: pair.correct,
        side: 'en',
        state: 'down',
      });
    });
    setCards(shuffle(deck));
    setFlipped([]);
    setMoves(0);
    setMatches(0);
    setRoundStart(performance.now());
    setLockBoard(false);
  }

  function startGame() {
    setRound(1);
    setTotalMatches(0);
    setScore(0);
    buildRound(1);
    setPhase('playing');
    sfxRef.current.play('start');
  }

  function onCardClick(card: Card) {
    if (lockBoard) return;
    if (card.state !== 'down') return;
    if (flipped.length >= 2) return;

    sfxRef.current.play('tap');
    setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, state: 'up' } : c)));
    const next = [...flipped, card.id];
    setFlipped(next);

    if (next.length === 2) {
      setMoves((m) => m + 1);
      const [aId, bId] = next;
      const a = cards.find((c) => c.id === aId);
      const b = cards.find((c) => c.id === bId);
      if (!a || !b) return;

      // A moment later, reveal result
      setLockBoard(true);
      if (a.pairId === b.pairId && a.side !== b.side) {
        // Match!
        window.setTimeout(() => {
          setCards((prev) =>
            prev.map((c) =>
              c.pairId === a.pairId ? { ...c, state: 'matched' } : c,
            ),
          );
          setFlipped([]);
          setLockBoard(false);
          setMatches((m) => m + 1);
          setTotalMatches((m) => m + 1);
          setScore((s) => s + XP_PER_MATCH);
          sfxRef.current.play('xp-gain');
        }, 360);
      } else {
        // No match — flash red briefly, then flip back
        setCards((prev) =>
          prev.map((c) => (c.id === a.id || c.id === b.id ? { ...c, state: 'wrong' } : c)),
        );
        sfxRef.current.play('hit');
        window.setTimeout(() => {
          setCards((prev) =>
            prev.map((c) =>
              c.id === a.id || c.id === b.id ? { ...c, state: 'down' } : c,
            ),
          );
          setFlipped([]);
          setLockBoard(false);
        }, 800);
      }
    }
  }

  // Advance round when all pairs matched
  useEffect(() => {
    if (phase !== 'playing') return;
    if (matches < PAIRS_PER_ROUND) return;
    // Time bonus: faster = more XP
    const elapsed = (performance.now() - roundStart) / 1000;
    const bonus = Math.max(
      0,
      Math.floor(XP_TIME_BONUS_CAP * (1 - Math.min(1, elapsed / 80))),
    );
    setScore((s) => s + bonus);
    sfxRef.current.play('level-up');

    if (round >= TOTAL_ROUNDS) {
      window.setTimeout(() => {
        const finalScore = score + bonus; // setScore ainda pode não ter commitado
        const result = commitScore(HIGH_SCORE_ID, finalScore);
        setHighScore(result.best);
        setNewRecord(result.isNew);
        setPhase('over');
      }, 900);
      return;
    }
    // Next round after a short breather
    const t = window.setTimeout(() => {
      const next = round + 1;
      setRound(next);
      buildRound(next);
    }, 900);
    return () => clearTimeout(t);
  }, [matches, phase, round, roundStart]);

  // Live elapsed seconds for display
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (phase !== 'playing') return;
    const id = window.setInterval(() => forceTick((t) => t + 1), 250);
    return () => clearInterval(id);
  }, [phase]);
  const elapsedSec = phase === 'playing'
    ? Math.max(0, Math.floor((performance.now() - roundStart) / 1000))
    : 0;

  const xpEarned = useMemo(() => score, [score]);

  return (
    <div className="sl-minigame sl-minigame--portrait">
      <button
        type="button"
        className="sl-minigame__close"
        onClick={() =>
          onExit({ score, rounds: round - 1, moves, xp: xpEarned })
        }
        aria-label="Fechar jogo"
      >
        ×
      </button>

      <div className="sl-flappy__sfx">
        <SfxToggle variant="icon" />
      </div>

      {phase === 'menu' ? (
        <div className="sl-flappy__menu">
          <h2>
            <span aria-hidden>🃏</span> Word Match
          </h2>
          <p>
            Vire duas cartas pra formar o par <strong>português ↔ inglês</strong>.
            Acertou, elas ficam viradas. Errou, vira de volta. Quanto mais rápido
            terminar a rodada, mais XP ganha de bônus.
          </p>
          {highScore > 0 ? (
            <p className="sl-flappy__record">
              🏆 Seu recorde: <strong>{highScore} XP</strong>
            </p>
          ) : null}
          <div className="sl-flappy__menu-actions">
            <button
              type="button"
              className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
              onClick={() => { setNewRecord(false); startGame(); }}
            >
              Começar
            </button>
          </div>
        </div>
      ) : null}

      {phase === 'playing' ? (
        <div className="sl-wmatch">
          <div className="sl-wmatch__hud">
            <span className="sl-wmatch__hud-item">
              Rodada <strong>{round}</strong>/{TOTAL_ROUNDS}
            </span>
            <span className="sl-wmatch__hud-item">
              Pares <strong>{matches}</strong>/{PAIRS_PER_ROUND}
            </span>
            <span className="sl-wmatch__hud-item">{elapsedSec}s</span>
            <span className="sl-wmatch__hud-item sl-wmatch__hud-item--xp">
              {score} XP
            </span>
          </div>
          <div className="sl-wmatch__grid">
            {cards.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`sl-wmatch__card sl-wmatch__card--${c.state} sl-wmatch__card--${c.side}`}
                onClick={() => onCardClick(c)}
                aria-label={c.state === 'down' ? 'Carta virada para baixo' : c.text}
              >
                <span className="sl-wmatch__inner">
                  <span className="sl-wmatch__face sl-wmatch__face--back" aria-hidden>
                    <span className="sl-wmatch__logo">G</span>
                  </span>
                  <span className="sl-wmatch__face sl-wmatch__face--front">
                    <span className="sl-wmatch__side-tag">
                      {c.side === 'pt' ? 'PT' : 'EN'}
                    </span>
                    <span className="sl-wmatch__text">{c.text}</span>
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {phase === 'over' ? (
        <div className="sl-flappy__over">
          <h2>
            <span aria-hidden style={{ marginRight: 8 }}><SlinguAvatar size={48} mood="happy" wings /></span>
            {newRecord ? '🏆 Novo recorde!' : 'Completou!'}
          </h2>
          <p>
            <strong>{totalMatches}</strong> pares · <strong>{moves}</strong> jogadas
            <br />
            Ganho: <strong>+{xpEarned} XP</strong>
          </p>
          <p className="sl-flappy__record">
            🏆 Recorde: <strong>{highScore} XP</strong>
            {newRecord ? ' ✨' : null}
          </p>
          <button
            type="button"
            className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
            onClick={() =>
              onExit({ score, rounds: TOTAL_ROUNDS, moves, xp: xpEarned })
            }
          >
            Voltar pro app
          </button>
        </div>
      ) : null}
    </div>
  );
}
